import { AgGridTable } from 'components/AgGrid/AgGridTable';
import { getRowId } from 'components/AgGrid/utils';
import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ModalDialog } from 'components/ModalDialog';
import { AG_GRID_ICON_SETUP } from 'constantsProject';
import { RequestStatus } from 'features/request/requestSlice';
import type { ChapterEnum, WorkOrderChangeStatusEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useMethodsConfirmationDialog } from 'hooks/useMethodsConfirmationDialog';
import { cnMixSpace, Loader } from 'uikit';

import type React from 'react';
import { useMemo, useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { CreateEditMtrDialog } from '../CreateEditMtrDialog';

import { useApplyMtr } from './hooks/useApplyMtr';
import { useUerMtrRowsForComparison } from './hooks/useUerMtrRowsForComparison';
import { useUerMtrRowsForComparisonColumns } from './hooks/useUerMtrRowsForComparisonColumns';

import './MatchMtrDialog.css';

interface IMatchMtrDialogProps {
    contractId: number;
    refreshProject: () => void;
    onMatchApply: () => any;
    chapter?: ChapterEnum | null;
    workOrderStatus?: WorkOrderStatusEnum | WorkOrderChangeStatusEnum;
    isChange: boolean;
    priceListId?: number;
    workOrderPriceId?: number;
    refreshTable?: () => void;
}

const style = { width: 1280 };

export const MatchMtrDialog: React.FC<IMatchMtrDialogProps> = (props) => {
    const { workOrderPriceId: workOrderPriceIdParams, priceListId: priceListIdParams } = useParams();

    const {
        contractId,
        onMatchApply,
        refreshProject,
        chapter,
        workOrderStatus,
        isChange,
        priceListId: priceListIdProps,
        workOrderPriceId: workOrderPriceIdProps,
        refreshTable,
    } = props;

    const priceListId = useMemo(
        () => priceListIdProps ?? Number(priceListIdParams),
        [priceListIdParams, priceListIdProps],
    );

    const workOrderPriceId = useMemo(
        () => workOrderPriceIdProps ?? Number(workOrderPriceIdParams),
        [workOrderPriceIdParams, workOrderPriceIdProps],
    );

    const { uerMtrRowsForComparisonItems, uerMtrRowsForComparisonStatus, changeFilterHandler } =
        useUerMtrRowsForComparison({
            workOrderPriceId,
        });

    const { columns, checkedMtrRowId } = useUerMtrRowsForComparisonColumns({
        contractId,
        mtrRowId: priceListId,
        changeFilterHandler,
    });

    const { isApplying, onModalConfirm } = useApplyMtr({
        checkedMtrRowId,
        onMatchApply,
        refreshProject,
        workOrderPriceId,
        workOrderStatus,
        isChange,
    });

    const { isOpenConfirmationDialog, handleOpenConfirmationDialog, handleCloseConfirmationDialog, handleActionQuery } =
        useMethodsConfirmationDialog({
            actionFn: onMatchApply,
        });

    const onModalClose = useCallback(() => {
        if (checkedMtrRowId) {
            handleOpenConfirmationDialog();
        } else {
            onMatchApply?.();
        }
    }, [checkedMtrRowId, handleOpenConfirmationDialog, onMatchApply]);

    return (
        <>
            <ModalDialog
                title="Сопоставление с прейскурантом МТР"
                isOpen
                hasOverlay
                withTitleCloseButton
                onClose={onModalClose}
                onConfirm={onModalConfirm}
                btnConfirmLabel="Сопоставить"
                btnCancelLabel="Отменить"
                leftPanel={
                    <CreateEditMtrDialog
                        onCloseMatchDialog={onMatchApply}
                        chapter={chapter as ChapterEnum}
                        workOrderStatus={workOrderStatus}
                        isChange={isChange}
                        mtrId={priceListId}
                        workOrderChangePriceId={workOrderPriceId}
                        refreshTableDataHandler={refreshTable}
                    />
                }
                isCommit={isApplying}
                btnConfirmDisabled={!checkedMtrRowId}
                style={style}
                isOnClickOutsideDisable
                buttonSize="m"
            >
                <div id="MatchMtrDialog">
                    <div
                        className={`${cnMixSpace({ pT: 'm' })} ag-theme-alpine WorkOrderChangePrice`}
                        style={{ height: '50vh' }}
                    >
                        {uerMtrRowsForComparisonStatus === RequestStatus.FETCHING ? (
                            <Loader />
                        ) : (
                            <AgGridTable
                                rowData={uerMtrRowsForComparisonItems}
                                columnDefs={columns as any}
                                overlayNoRowsTemplate="Нет данных"
                                getRowId={getRowId}
                                icons={AG_GRID_ICON_SETUP}
                                headerHeight={70}
                                suppressDragLeaveHidesColumns
                                defaultColDef={{
                                    resizable: true,
                                }}
                                tooltipShowDelay={200}
                            />
                        )}
                    </div>
                </div>
            </ModalDialog>
            <ConfirmationDialog
                textHeader="Отмена действия"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                cancelButtonText="Не отменять"
                confirmButtonText="Отменить"
                isOpened={isOpenConfirmationDialog}
                onSubmit={handleActionQuery}
                onClose={handleCloseConfirmationDialog}
            />
        </>
    );
};

MatchMtrDialog.displayName = 'MatchMtrDialog';
